/* eslint-env browser */
// Customer / driver details › Wallet (wave 3, W2_Portal_Tables look): the
// balances as figure tiles, then the wallet history in the modern table.
//
// Read-only, and the figures are the same as before:
// - driver: Total = walletBalance + topUpBalance (when both are set), the
//   top-up balance (or, on the daily flat rate, the days remaining from
//   getFlatRateDaysRemaining with the top-up balance under it, as the
//   driver app shows it) and the earning balance (walletBalance);
// - customer: the wallet balance.
// Data, as before: the user record handed in by the details page (live
// through its one-user listener) and ONE read of this person's wallet
// history (fetchUserWalletHistory) when the tab opens or the record
// changes. Exports for admin and finance, CSV only with critical edits on.
// "← Back" is the page's own (top left).
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import SyncAltRoundedIcon from '@mui/icons-material/SyncAltRounded';
import { api } from 'common';
import { tokens as c } from '../theme';
import DashboardCard from '../components/DashboardCard';
import { DataTable, calmCaps, formatMoney } from '../components/modern';

const NUMBER_FORMAT = new Intl.NumberFormat('en-US');
const fmt = (n) => NUMBER_FORMAT.format(n || 0);

// "Transaction Id :" is stored with a colon for forms; a column title has none.
const noColon = (text) => String(text || '').replace(/\s*:\s*$/, '');

const typeStatus = (type, t) => {
  if (type === 'Credit') return { tone: 'success', icon: <AddRoundedIcon />, label: t('Credit', { defaultValue: 'Credit' }) };
  if (type === 'Debit') return { tone: 'error', icon: <RemoveRoundedIcon />, label: t('Debit', { defaultValue: 'Debit' }) };
  return { tone: 'amber', icon: <SyncAltRoundedIcon />, label: type ? t(type, { defaultValue: type }) : '' };
};

const UserWalletDetails = (props) => {
  const { data } = props;
  const { t, i18n } = useTranslation();
  const { fetchUserWalletHistory } = api;
  const settingsdata = useSelector((state) => state.settingsdata);
  const auth = useSelector((state) => state.auth);
  const walletHistory = useSelector((state) => state.auth.walletHistory);
  const cars = useSelector((state) => state.cartypes.cars);
  const dispatch = useDispatch();
  const settings = settingsdata.settings || {};
  // Which person's history has arrived: until it has, the table shows grey
  // rows (the store slot may still hold the person opened before).
  const [loadedFor, setLoadedFor] = useState(null);

  useEffect(() => {
    let alive = true;
    const uid = data?.id;
    Promise.resolve(dispatch(fetchUserWalletHistory(uid))).finally(() => {
      if (alive) setLoadedFor(uid === undefined ? null : uid);
    });
    return () => {
      alive = false;
    };
  }, [data, dispatch, fetchUserWalletHistory]);

  const role = auth.profile && auth.profile.usertype;
  const canExport = role === 'admin' || role === 'finance';
  const isDriver = data?.usertype === 'driver';
  const money = (value) => formatMoney(value === undefined || value === null || value === '' ? 0 : value, settings, true);
  const negative = (value) => parseFloat(value) < 0;

  // ── Balance tiles (the same figures as before) ─────────────────────────
  const total =
    data?.walletBalance !== undefined && data?.topUpBalance !== undefined
      ? parseFloat(data.walletBalance || 0) + parseFloat(data.topUpBalance || 0)
      : 0;
  const topUp = data?.topUpBalance !== undefined ? data.topUpBalance : 0;
  const flatDaily = isDriver && data?.commissionMode === 'flat_daily';
  // Mirrors the mobile wallet tile: days remaining leads (that's what gates
  // dispatch), the raw top-up balance stays visible underneath.
  const days = flatDaily ? api.getFlatRateDaysRemaining(data, cars) : null;
  const wallet = data?.walletBalance !== undefined ? data.walletBalance : 0;
  const signed = (value) => (
    <Box component="span" sx={{ color: negative(value) ? c.DANGER_TEXT : 'inherit' }}>
      {money(value)}
    </Box>
  );

  const tiles = isDriver
    ? [
        <DashboardCard key="total" icon={<AccountBalanceWalletOutlinedIcon />} hue="accent" title={t('total_balance')} value={money(Number(total.toFixed(2)))} />,
        flatDaily ? (
          <DashboardCard
            key="days"
            icon={<EventAvailableOutlinedIcon />}
            hue="amber"
            title={t('flat_rate_days_remaining', { defaultValue: 'Days remaining' })}
            value={
              <Box component="span" sx={{ color: days !== null && days < 1 ? c.DANGER_TEXT : 'inherit' }}>
                {`${days === null ? '—' : days} ${t('flat_rate_days_unit', { defaultValue: 'days' })}`}
              </Box>
            }
            caption={
              <Box component="span" sx={{ color: negative(topUp) ? c.DANGER_TEXT : 'inherit' }}>
                {`${t('topup_balance')}: ${money(topUp)}`}
              </Box>
            }
          />
        ) : (
          <DashboardCard key="topup" icon={<SavingsOutlinedIcon />} hue="info" title={t('topup_balance')} value={signed(topUp)} />
        ),
        <DashboardCard key="earning" icon={<PaymentsOutlinedIcon />} hue="success" title={t('earning_balance')} value={money(wallet)} />,
      ]
    : [<DashboardCard key="wallet" icon={<AccountBalanceWalletOutlinedIcon />} hue="success" title={t('wallet_ballance')} value={money(wallet)} />];

  // ── History table ──────────────────────────────────────────────────────
  const rows = Array.isArray(walletHistory) ? walletHistory : [];
  const loading = loadedFor === null || loadedFor !== (data?.id === undefined ? null : data.id);
  const title = calmCaps(t('transaction_history_title', { defaultValue: 'Wallet transaction history' }));

  const columns = [
    { id: 'date', title: t('requestDate'), field: 'date', type: 'datetime' },
    { id: 'amount', title: t('amount'), field: 'amount', type: 'money', render: (row) => money(row.amount || 0) },
    {
      id: 'transaction_id',
      title: noColon(t('transaction_id')),
      field: 'transaction_id',
      render: (row) => row.transaction_id || row.txRef || '—',
      searchValue: (row) => row.transaction_id || row.txRef || '',
      sortValue: (row) => row.transaction_id || row.txRef || '',
      exportValue: (row) => row.transaction_id || row.txRef || '',
    },
    { id: 'type', title: t('type'), field: 'type', type: 'status', status: (row) => typeStatus(row.type, t) },
  ];

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: isDriver ? { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' } : { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 360px)' },
        }}
      >
        {tiles}
      </Box>
      <DataTable
        title={title}
        rows={loading ? [] : rows}
        columns={columns}
        loading={loading}
        countText={({ shown, total: all, query, loading: busy }) =>
          busy
            ? t('loading', { defaultValue: 'Loading…' })
            : query
              ? t('table_count_shown', { shown: fmt(shown), total: fmt(all), defaultValue: '{{shown}} of {{total}}' })
              : t('wallet_tx_count', {
                  count: all,
                  n: fmt(all),
                  defaultValue_one: '{{n}} transaction',
                  defaultValue_other: '{{n}} transactions',
                  defaultValue: '{{n}} transactions',
                })
        }
        prefsKey="user_wallet_history"
        exportCsv={canExport && !!settings.AllowCriticalEditsAdmin}
        exportPrint={canExport}
        exportFileName="wallet_history"
        exportTitle={title}
        exportSubtitle={`${data?.firstName || ''} ${data?.lastName || ''}`.trim()}
        minWidth={640}
        maxHeight={null}
        emptyState={({ query }) =>
          query ? null : { icon: <ReceiptLongOutlinedIcon />, title: t('wallet_tx_empty', { defaultValue: 'No wallet transactions yet' }) }
        }
      />
    </Box>
  );
};

export default UserWalletDetails;
